import { useEffect } from 'react';
import { useForm } from 'react-hook-form';
import type { Session } from '@supabase/supabase-js';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from './ui/dialog';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import { LoadingSpinner } from './ui/loading-spinner';
import { useToast } from './ui/toast';
import { useSession } from '../context/session-context';
import { useWorkspace } from '../context/workspace-context';
import { useUpgradeRequest } from '../hooks/use-upgrade-request';
import { Tier } from '../constants/tier';
import { TIER_LIMITS } from '../constants/tier-limits';

export const UPGRADE_REASON = {
  COURSE_LIMIT: 'course-limit',
  USER_LIMIT: 'user-limit',
  BULK_IMPORT_NOT_ALLOWED: 'bulk-import-not-allowed',
} as const;

export type UpgradeReason =
  (typeof UPGRADE_REASON)[keyof typeof UPGRADE_REASON];

type FormFields = {
  name: string;
  email: string;
  message: string;
};

type Props = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  reason?: UpgradeReason;
};

export default function UpgradePopup({ open, onOpenChange, reason }: Props) {
  const { session } = useSession();
  const { currentWorkspace } = useWorkspace();
  const { showToast } = useToast();
  const upgradeRequest = useUpgradeRequest();
  const description = describeReason(reason, currentWorkspace?.tier);

  const {
    register,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm<FormFields>({
    defaultValues: defaultsFromSession(session),
  });

  useEffect(() => {
    if (open) reset(defaultsFromSession(session));
  }, [open, session, reset]);

  const onSubmit = ({ name, email, message }: FormFields) => {
    if (!currentWorkspace) return;
    upgradeRequest.mutate(
      { name, email, message, organisationId: currentWorkspace.id },
      {
        onSuccess: () => {
          showToast("Upgrade request sent. We'll be in touch.", 'success');
          onOpenChange(false);
        },
        onError: (error) => showToast(error.message, 'error'),
      },
    );
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[440px]">
        <DialogHeader>
          <DialogTitle>Upgrade Request</DialogTitle>
          <DialogDescription>{description}</DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
          <NameField register={register} error={errors.name?.message} />
          <EmailField register={register} error={errors.email?.message} />
          <MessageField register={register} error={errors.message?.message} />
          <SubmitButton isLoading={upgradeRequest.isPending} />
        </form>
      </DialogContent>
    </Dialog>
  );
}

function describeReason(
  reason: UpgradeReason | undefined,
  tier: Tier | undefined,
): string {
  const fallback = "Tell us about your needs and we'll get back to you.";
  if (!reason || !tier) return fallback;
  const limits = TIER_LIMITS[tier];
  if (!limits) return fallback;

  if (reason === UPGRADE_REASON.COURSE_LIMIT) {
    return `Your tier allows ${limits.maxPublishedCourses} published course${limits.maxPublishedCourses === 1 ? '' : 's'} at a time. Upgrade to publish more.`;
  }
  if (reason === UPGRADE_REASON.USER_LIMIT) {
    return `You've reached your tier's user limit of ${limits.maxUsers}. Upgrade to add more team members.`;
  }
  if (reason === UPGRADE_REASON.BULK_IMPORT_NOT_ALLOWED) {
    return 'Bulk user import is available on the Business tier. Upgrade to enable it.';
  }
  return fallback;
}

function defaultsFromSession(session: Session | null): FormFields {
  if (!session?.user) {
    return { name: '', email: '', message: '' };
  }
  const metadata = session.user.user_metadata as {
    name?: string;
    surname?: string;
  };
  const fullName = metadata?.surname
    ? `${metadata.name ?? ''} ${metadata.surname}`.trim()
    : (metadata?.name ?? '');
  return {
    name: fullName,
    email: session.user.email ?? '',
    message: '',
  };
}

type FieldProps = {
  register: ReturnType<typeof useForm<FormFields>>['register'];
  error?: string;
};

function NameField({ register, error }: FieldProps) {
  return (
    <div className="space-y-1.5">
      <Label htmlFor="name" className="text-sm text-title font-medium">
        Name
      </Label>
      <Input
        id="name"
        {...register('name', { required: 'Name is required' })}
        className="h-11"
      />
      {error && <p className="text-xs text-red-500">{error}</p>}
    </div>
  );
}

function EmailField({ register, error }: FieldProps) {
  return (
    <div className="space-y-1.5">
      <Label htmlFor="email" className="text-sm text-title font-medium">
        Email
      </Label>
      <Input
        id="email"
        type="email"
        {...register('email', { required: 'Email is required' })}
        className="h-11"
      />
      {error && <p className="text-xs text-red-500">{error}</p>}
    </div>
  );
}

function MessageField({ register, error }: FieldProps) {
  return (
    <div className="space-y-1.5">
      <Label htmlFor="message" className="text-sm text-title font-medium">
        Message
      </Label>
      <Textarea
        id="message"
        rows={4}
        placeholder="Tell us a bit about what you need."
        {...register('message', { required: 'Message is required' })}
      />
      {error && <p className="text-xs text-red-500">{error}</p>}
    </div>
  );
}

function SubmitButton({ isLoading }: { isLoading: boolean }) {
  return (
    <Button
      type="submit"
      disabled={isLoading}
      className="w-full h-12 bg-accent-purple hover:bg-accent-purple/90 text-white rounded-full"
    >
      {isLoading ? <LoadingSpinner /> : 'Send request'}
    </Button>
  );
}
